import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

/**
 * ConfirmDialog - modal défensif rendu via portal dans document.body
 * Pour les actions destructives (delete user, delete ticket, etc.).
 * Utilise le même pattern que AssignButton : portail + zIndex 9999 + Escape + click backdrop.
 *
 * Props:
 *  - open: boolean
 *  - onCancel: () => void
 *  - onConfirm: () => void
 *  - title: string
 *  - message: string (corps)
 *  - confirmLabel: string
 *  - cancelLabel: string (optional)
 *  - destructive: boolean (red confirm button)
 *  - submitting: boolean
 */
export default function ConfirmDialog({ open, onCancel, onConfirm, title, message, confirmLabel, cancelLabel, destructive = true, submitting = false }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onCancel(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open, onCancel, submitting]);

  if (!open || typeof document === 'undefined') return null;

  const modal = (
    <div
      data-testid="confirm-dialog-backdrop"
      onClick={() => { if (!submitting) onCancel(); }}
      role="alertdialog"
      aria-modal="true"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(2px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        data-testid="confirm-dialog"
        onClick={e => e.stopPropagation()}
        className="animate-fade-in"
        style={{
          width: '100%', maxWidth: '440px',
          background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '20px 20px 12px', display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between' }}>
          <div className="flex items-start gap-3 min-w-0">
            {destructive && (
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <AlertTriangle size={20} style={{ color: '#EF4444' }} />
              </div>
            )}
            <div className="min-w-0">
              <h3 data-testid="confirm-dialog-title" className="text-base font-bold text-white">{title}</h3>
              {message ? <p data-testid="confirm-dialog-message" className="text-sm mt-1" style={{ color: '#9CA3AF' }}>{message}</p> : null}
            </div>
          </div>
          {!submitting && (
            <button data-testid="confirm-dialog-close" onClick={onCancel} aria-label="Fermer" className="p-1 rounded-lg hover:bg-white/5 flex-shrink-0" style={{ color: '#9CA3AF' }}>
              <X size={18} />
            </button>
          )}
        </div>
        <div style={{ padding: '12px 20px 20px', display: 'flex', gap: 8, borderTop: '1px solid rgba(255,255,255,0.06)', background: '#1A1A2E', flexDirection: 'row' }} className="flex-col sm:flex-row">
          <button
            data-testid="confirm-dialog-cancel"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#9CA3AF', minHeight: 44 }}
          >
            {cancelLabel || 'Annuler'}
          </button>
          <button
            data-testid="confirm-dialog-confirm"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold"
            style={{ background: destructive ? '#EF4444' : '#F77F00', color: '#fff', opacity: submitting ? 0.5 : 1, minHeight: 44 }}
          >
            {submitting ? '...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
